import { useEffect, useState } from "react";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";

/**
 * Premium 2-second splash / loader animation.
 *
 * Shown on:
 *   • Initial site entry
 *   • Every browser refresh / hard navigation
 *
 * Hidden during internal React-Router transitions.
 *
 * Visual layers (bottom → top):
 *   1. Deep cinematic gradient background
 *   2. Subtle grid pattern (low opacity)
 *   3. Radial spotlights (gold + emerald glow halos)
 *   4. Animated diagonal light sweep
 *   5. Floating particles (gold dust)
 *   6. Center: gold ring + brand mark
 *   7. Below: wordmark with shimmer
 *   8. Bottom: progress bar (0 → 100% over 1.6s)
 *
 * Reads `loader_url` from site settings. If present, the uploaded GIF is
 * shown inside the gold ring instead of the default "M" mark — the rest of
 * the cinematic frame stays the same so the splash always feels on-brand.
 */
export default function SplashScreen() {
  const { settings } = useSiteSettings();
  const loaderUrl = settings?.loader_url || "";
  const siteName = settings?.site_name || "Maxlive88";

  const [phase, setPhase] = useState("show"); // 'show' | 'fade' | 'gone'

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("fade"), 4400);
    const t2 = setTimeout(() => setPhase("gone"), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (phase === "gone") return null;

  const m = (siteName || "").match(/^(.*?)(\d+)$/);
  const head = m ? m[1] : siteName;
  const tail = m ? m[2] : "";

  return (
    <div
      data-testid="splash-screen"
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        overflow: "hidden",
        background:
          "radial-gradient(120% 90% at 50% 30%, #102236 0%, #07101a 55%, #03070c 100%)",
        opacity: phase === "fade" ? 0 : 1,
        transform: phase === "fade" ? "scale(1.04)" : "scale(1)",
        transition: "opacity 600ms ease, transform 600ms ease",
        pointerEvents: phase === "fade" ? "none" : "auto",
        fontFamily: "'Bebas Neue', 'Oswald', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* Subtle grid pattern */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(ellipse 60% 50% at 50% 50%, black 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 60% 50% at 50% 50%, black 30%, transparent 80%)",
        }}
      />

      {/* Emerald glow halo */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "720px",
          height: "720px",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(closest-side, rgba(25,165,92,0.22) 0%, rgba(25,165,92,0) 70%)",
          filter: "blur(10px)",
          pointerEvents: "none",
          animation: "splash-halo 2.2s ease-out forwards",
        }}
      />

      {/* Gold spotlight */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "44%",
          left: "50%",
          width: "440px",
          height: "440px",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(closest-side, rgba(255,200,80,0.28) 0%, rgba(255,200,80,0) 70%)",
          filter: "blur(6px)",
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      />

      {/* Diagonal light sweep */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.07) 50%, transparent 65%)",
          mixBlendMode: "screen",
          animation: "splash-sweep 2.3s 0.1s cubic-bezier(.2,.7,.2,1) forwards",
          transform: "translateX(-100%)",
        }}
      />

      {/* Floating gold particles */}
      <Particles />

      {/* Vignette */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.45) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Center stage */}
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "26px",
        }}
      >
        {/* Concentric rings + mark */}
        <div
          style={{
            position: "relative",
            width: "168px",
            height: "168px",
            animation: "splash-rise 700ms cubic-bezier(.2,.7,.2,1) both",
          }}
        >
          {/* Outer pulse ring */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: "-14px",
              borderRadius: "50%",
              border: "1px solid rgba(255,210,90,0.35)",
              boxShadow:
                "0 0 60px rgba(255,180,32,0.20), inset 0 0 30px rgba(255,180,32,0.10)",
              animation: "splash-pulse 1.8s ease-in-out infinite",
            }}
          />
          {/* Spinning gold ring (gradient stroke via conic) */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              padding: "3px",
              background:
                "conic-gradient(from 0deg, transparent 0deg, #ffd766 90deg, #ffb020 180deg, rgba(255,180,32,0.4) 240deg, transparent 320deg)",
              animation: "splash-spin 1.6s linear infinite",
              WebkitMask:
                "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              filter: "drop-shadow(0 0 14px rgba(255,180,32,0.55))",
            }}
          />
          {/* Inner subtle ring */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: "16px",
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.08)",
              background:
                "radial-gradient(closest-side, rgba(25,165,92,0.32) 0%, rgba(25,165,92,0.06) 60%, transparent 100%)",
              boxShadow: "inset 0 0 24px rgba(25,165,92,0.25)",
            }}
          />
          {/* Loader image or default M mark */}
          <div
            style={{
              position: "absolute",
              inset: "26px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              background:
                "radial-gradient(closest-side, #0e1a26 0%, #060c14 100%)",
              boxShadow:
                "inset 0 2px 6px rgba(0,0,0,0.6), 0 12px 32px rgba(0,0,0,0.55)",
            }}
          >
            {loaderUrl ? (
              <img
                src={loaderUrl}
                alt={siteName}
                data-testid="splash-loader-image"
                style={{
                  width: "78%",
                  height: "78%",
                  objectFit: "contain",
                  animation: "splash-mark 1.6s ease-in-out infinite",
                }}
              />
            ) : (
              <PremiumMMark />
            )}
          </div>
          {/* Orbiting dots on the ring */}
          {[0, 120, 240].map((deg) => (
            <span
              key={deg}
              aria-hidden
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "10px",
                height: "10px",
                marginTop: "-5px",
                marginLeft: "-5px",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at 30% 30%, #fff 0%, #ffe28b 35%, #d99a14 100%)",
                boxShadow: "0 0 12px rgba(255,200,80,0.85)",
                transform: `rotate(${deg}deg) translateX(84px)`,
                transformOrigin: "center center",
                animation: `splash-orbit 1.8s linear ${deg / 360}s infinite`,
              }}
            />
          ))}
        </div>

        {/* Wordmark with shimmer */}
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "baseline",
            padding: "4px 6px",
            animation: "splash-rise 800ms 120ms cubic-bezier(.2,.7,.2,1) both",
            fontFamily:
              "'Bebas Neue', 'Oswald', 'Inter', system-ui, sans-serif",
            fontWeight: 800,
            fontSize: "clamp(36px, 5.2vw, 58px)",
            lineHeight: 1,
            letterSpacing: "0.005em",
            overflow: "hidden",
          }}
        >
          <span
            style={{
              background:
                "linear-gradient(180deg, #ffffff 0%, #e8eef5 60%, #a9b8c8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: "drop-shadow(0 2px 12px rgba(255,255,255,0.18))",
            }}
          >
            {head}
          </span>
          {tail && (
            <span
              style={{
                marginLeft: "2px",
                background:
                  "linear-gradient(180deg, #ff6680 0%, #ff2740 55%, #a8001a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0 2px 12px rgba(255,39,64,0.55))",
              }}
            >
              {tail}
            </span>
          )}
          {/* Shimmer overlay */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: "55%",
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0) 100%)",
              mixBlendMode: "overlay",
              animation: "splash-shimmer 1.8s 0.4s ease-in-out infinite",
            }}
          />
        </div>

        {/* Kicker tagline */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            opacity: 0.78,
            animation: "splash-rise 900ms 220ms cubic-bezier(.2,.7,.2,1) both",
          }}
        >
          <span style={{ width: 18, height: 1, background: "rgba(255,210,90,0.5)" }} />
          <span
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "11px",
              letterSpacing: "0.42em",
              fontWeight: 700,
              color: "rgba(255,235,180,0.78)",
              textTransform: "uppercase",
            }}
          >
            Play Smart · Win Big
          </span>
          <span style={{ width: 18, height: 1, background: "rgba(255,210,90,0.5)" }} />
        </div>

        {/* Slim progress bar */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            bottom: "12vh",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(260px, 50vw)",
            height: "3px",
            borderRadius: "999px",
            background: "rgba(255,255,255,0.06)",
            overflow: "hidden",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "0%",
              background:
                "linear-gradient(90deg, #19a55c 0%, #ffd766 55%, #ff2740 100%)",
              boxShadow:
                "0 0 14px rgba(255,200,80,0.55), 0 0 6px rgba(25,165,92,0.55)",
              animation: "splash-fill 4.4s cubic-bezier(.4,.0,.2,1) forwards",
            }}
          />
        </div>

        {/* Footer signature */}
        <div
          style={{
            position: "absolute",
            bottom: "5vh",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            opacity: 0.55,
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: "10px",
            letterSpacing: "0.28em",
            color: "rgba(255,255,255,0.55)",
            textTransform: "uppercase",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#19a55c",
              boxShadow: "0 0 10px #19a55c",
            }}
          />
          Loading experience
        </div>
      </div>

      <style>{`
        @keyframes splash-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes splash-pulse {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50%      { transform: scale(1.05); opacity: 1; }
        }
        @keyframes splash-mark {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }
        @keyframes splash-orbit {
          from { transform: rotate(0deg)   translateX(84px); }
          to   { transform: rotate(360deg) translateX(84px); }
        }
        @keyframes splash-rise {
          from { opacity: 0; transform: translateY(16px) scale(0.96); filter: blur(4px); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    filter: blur(0); }
        }
        @keyframes splash-shimmer {
          0%   { transform: translateX(-120%) skewX(-18deg); }
          100% { transform: translateX(220%)  skewX(-18deg); }
        }
        @keyframes splash-sweep {
          0%   { transform: translateX(-100%); opacity: 0; }
          25%  { opacity: 1; }
          100% { transform: translateX(100%);  opacity: 0; }
        }
        @keyframes splash-halo {
          from { opacity: 0; transform: translate(-50%,-50%) scale(0.85); }
          to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes splash-fill {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes splash-particle {
          0%   { opacity: 0; transform: translate(0,0) scale(0.6); }
          15%  { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ---------- Premium animated "M" monogram (crown + metallic finish) ---------- */
const PremiumMMark = () => (
  <svg
    viewBox="0 0 200 200"
    width="86%"
    height="86%"
    role="img"
    aria-label="Maxlive88 monogram"
    style={{
      overflow: "visible",
      filter: "drop-shadow(0 8px 22px rgba(255,180,32,0.55))",
      animation: "splash-mark 2.4s ease-in-out infinite",
    }}
  >
    <defs>
      {/* Bright top → deep gold bottom metallic fill */}
      <linearGradient id="m-gold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#fff7d4" />
        <stop offset="22%" stopColor="#ffd766" />
        <stop offset="55%" stopColor="#e8a51b" />
        <stop offset="80%" stopColor="#a16808" />
        <stop offset="100%" stopColor="#5b3a02" />
      </linearGradient>
      {/* Soft inner shine */}
      <linearGradient id="m-shine" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="rgba(255,255,255,0.85)" />
        <stop offset="35%" stopColor="rgba(255,255,255,0.18)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </linearGradient>
      {/* Outer rim — darker for embossed depth */}
      <linearGradient id="m-rim" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#7a4a02" />
        <stop offset="100%" stopColor="#2a1801" />
      </linearGradient>
      {/* Diamond accent */}
      <radialGradient id="m-diamond" cx="50%" cy="40%" r="60%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="45%" stopColor="#bff0ff" />
        <stop offset="100%" stopColor="#0a6e8c" />
      </radialGradient>
      {/* Crown gold */}
      <linearGradient id="m-crown" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"  stopColor="#fff2a8" />
        <stop offset="45%" stopColor="#ffc94a" />
        <stop offset="100%" stopColor="#8a5f00" />
      </linearGradient>
      {/* Sparkle wipe */}
      <linearGradient id="m-sparkle" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"   stopColor="rgba(255,255,255,0)" />
        <stop offset="50%"  stopColor="rgba(255,255,255,0.95)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </linearGradient>
      {/* Clip to the M shape so the sparkle stays inside the letter */}
      <clipPath id="m-clip">
        <path d="M 36 158 L 36 50 L 60 50 L 100 112 L 140 50 L 164 50 L 164 158 L 142 158 L 142 88 L 110 138 L 90 138 L 58 88 L 58 158 Z" />
      </clipPath>
      {/* Soft glow filter */}
      <filter id="m-glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="2.2" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Crown above the M */}
    <g transform="translate(0,-2)" style={{ filter: "drop-shadow(0 2px 6px rgba(255,180,32,0.55))" }}>
      {/* Crown body */}
      <path
        d="M 60 38 L 72 18 L 86 32 L 100 12 L 114 32 L 128 18 L 140 38 L 140 46 L 60 46 Z"
        fill="url(#m-crown)"
        stroke="#5b3a02"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      {/* Crown bottom band */}
      <rect x="60" y="44" width="80" height="5" rx="1" fill="url(#m-crown)" stroke="#5b3a02" strokeWidth="1" />
      {/* Crown jewels (3 dots) */}
      <circle cx="78" cy="33" r="2.2" fill="#ff4757" stroke="#5b3a02" strokeWidth="0.6" />
      <circle cx="100" cy="22" r="2.6" fill="url(#m-diamond)" stroke="#0a4a5c" strokeWidth="0.6" />
      <circle cx="122" cy="33" r="2.2" fill="#19a55c" stroke="#0a4a02" strokeWidth="0.6" />
      {/* Crown highlight */}
      <path
        d="M 64 42 Q 80 39 100 39 Q 120 39 136 42"
        fill="none"
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="0.8"
        strokeLinecap="round"
      />
    </g>

    {/* M — outer dark rim (embossed depth) */}
    <path
      d="M 32 162 L 32 48 L 62 48 L 100 108 L 138 48 L 168 48 L 168 162 L 144 162 L 144 86 L 110 138 L 90 138 L 56 86 L 56 162 Z"
      fill="url(#m-rim)"
    />
    {/* M — gold metallic body */}
    <path
      d="M 36 158 L 36 50 L 60 50 L 100 112 L 140 50 L 164 50 L 164 158 L 142 158 L 142 88 L 110 138 L 90 138 L 58 88 L 58 158 Z"
      fill="url(#m-gold)"
    />
    {/* M — top inner highlight */}
    <g clipPath="url(#m-clip)" opacity="0.85">
      <path
        d="M 36 50 L 60 50 L 100 112 L 140 50 L 164 50 L 164 78 L 100 78 L 36 78 Z"
        fill="url(#m-shine)"
      />
    </g>
    {/* M — inner thin rim for jewel-cut feel */}
    <path
      d="M 36 158 L 36 50 L 60 50 L 100 112 L 140 50 L 164 50 L 164 158 L 142 158 L 142 88 L 110 138 L 90 138 L 58 88 L 58 158 Z"
      fill="none"
      stroke="rgba(255,250,220,0.45)"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    {/* Diamond accent in the center valley of the M */}
    <g transform="translate(100,124)" style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))" }}>
      <path
        d="M 0 -7 L 7 0 L 0 7 L -7 0 Z"
        fill="url(#m-diamond)"
        stroke="#0a4a5c"
        strokeWidth="0.8"
      />
      <path
        d="M -3.2 -2 L 0 -7 L 3.2 -2"
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="0.7"
        strokeLinecap="round"
      />
    </g>

    {/* Sweeping sparkle highlight across the M */}
    <g clipPath="url(#m-clip)">
      <rect
        x="-60"
        y="40"
        width="60"
        height="130"
        fill="url(#m-sparkle)"
        opacity="0.9"
        style={{
          animation: "splash-mark-sparkle 2.6s 0.4s ease-in-out infinite",
        }}
      />
    </g>

    {/* Star sparkles around the M */}
    <g filter="url(#m-glow)">
      <Sparkle x={26}  y={62}  size={3.2} delay={0}   />
      <Sparkle x={174} y={62}  size={3.2} delay={0.3} />
      <Sparkle x={20}  y={150} size={2.4} delay={0.6} />
      <Sparkle x={180} y={150} size={2.4} delay={0.9} />
      <Sparkle x={100} y={170} size={2.8} delay={1.2} />
    </g>

    <style>{`
      @keyframes splash-mark-sparkle {
        0%   { transform: translateX(0); opacity: 0; }
        25%  { opacity: 1; }
        100% { transform: translateX(260px); opacity: 0; }
      }
      @keyframes splash-mark-twinkle {
        0%, 100% { opacity: 0.2; transform: scale(0.85); }
        50%      { opacity: 1;   transform: scale(1.25); }
      }
    `}</style>
  </svg>
);

const Sparkle = ({ x, y, size = 3, delay = 0 }) => (
  <g
    transform={`translate(${x},${y})`}
    style={{
      transformOrigin: `${x}px ${y}px`,
      animation: `splash-mark-twinkle 1.6s ${delay}s ease-in-out infinite`,
    }}
  >
    <path
      d={`M 0 -${size * 2.2} L ${size * 0.55} -${size * 0.55} L ${size * 2.2} 0 L ${size * 0.55} ${size * 0.55} L 0 ${size * 2.2} L -${size * 0.55} ${size * 0.55} L -${size * 2.2} 0 L -${size * 0.55} -${size * 0.55} Z`}
      fill="#fff8d6"
    />
  </g>
);

/* ---------- Floating gold-dust particles ---------- */
const Particles = () => {
  // Deterministic but visually random scatter (avoids re-render flicker).
  const items = Array.from({ length: 28 }).map((_, i) => {
    const seed = i + 1;
    const left = (seed * 53) % 100;
    const top = (seed * 97) % 100;
    const size = 2 + ((seed * 7) % 4);
    const dx = (((seed * 31) % 60) - 30) + "px";
    const dy = -40 - ((seed * 13) % 40) + "px";
    const delay = ((seed * 41) % 1800) / 1000;
    const duration = 1.6 + ((seed * 17) % 1200) / 1000;
    const isGold = seed % 3 !== 0;
    return { id: i, left, top, size, dx, dy, delay, duration, isGold };
  });

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
      }}
    >
      {items.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: "50%",
            background: p.isGold
              ? "radial-gradient(circle at 30% 30%, #fff8d6 0%, #ffd766 45%, #b8820f 100%)"
              : "radial-gradient(circle at 30% 30%, #c8f5dd 0%, #21c46e 50%, #0a5a2f 100%)",
            boxShadow: p.isGold
              ? "0 0 10px rgba(255,200,80,0.85)"
              : "0 0 10px rgba(25,165,92,0.85)",
            opacity: 0,
            // CSS custom props consumed by the keyframes
            "--dx": p.dx,
            "--dy": p.dy,
            animation: `splash-particle ${p.duration}s ${p.delay}s ease-out infinite`,
          }}
        />
      ))}
    </div>
  );
};
